import { GlobalStyles, TextField, type TextFieldProps } from '@mui/material';
import React from 'react';

export interface AddressParts {
  line1: string;
  suburb: string;
  state: string;
  postcode: string;
}

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
  /** Called with the split-out parts of a chosen address, for forms that store them separately. */
  onPlaceParts?: (parts: AddressParts) => void;
};

export function addressParts(
  components: google.maps.GeocoderAddressComponent[] = [],
): AddressParts {
  const part = (type: string, short = false) => {
    const found = components.find((item) => item.types.includes(type));
    return (short ? found?.short_name : found?.long_name) ?? '';
  };
  return {
    line1: [
      part('subpremise') && `${part('subpremise')}/`,
      part('street_number'),
      ' ',
      part('route'),
    ]
      .join('')
      .trim(),
    suburb: part('locality') || part('sublocality'),
    state: part('administrative_area_level_1', true),
    postcode: part('postal_code'),
  };
}

let loader: Promise<void> | undefined;

const placesReady = () => Boolean(window.google?.maps?.places);

/**
 * Loads the Google Maps script with the Places library unless the host app already did (the
 * admin and investor apps load it at startup). The key is optional: without one the field is a
 * plain text input.
 */
function loadPlaces(): Promise<void> {
  if (placesReady()) return Promise.resolve();
  let key: string | undefined;
  try {
    key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || undefined;
  } catch {
    key = undefined;
  }
  if (!key) return Promise.reject(new Error('No Google Maps key'));
  loader ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loader = undefined;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });
  return loader;
}

/**
 * An address input with Google Place Autocomplete, restricted to Australian addresses. It
 * shows no map and keeps only the chosen address text. The widget supplies Google's required
 * "Powered by Google" attribution; the field stays a free text input if Google is unavailable.
 */
export default function AddressAutocompleteField({
  value,
  onChange,
  onPlaceParts,
  ...textFieldProps
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onPartsRef = React.useRef(onPlaceParts);
  onPartsRef.current = onPlaceParts;

  React.useEffect(() => {
    let cancelled = false;
    let listener: google.maps.MapsEventListener | undefined;
    loadPlaces()
      .then(() => {
        if (cancelled || !inputRef.current) return;
        const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: 'au' },
          types: ['address'],
          fields: onPartsRef.current
            ? ['formatted_address', 'address_components']
            : ['formatted_address'],
        });
        listener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place.formatted_address) return;
          onChangeRef.current(place.formatted_address);
          onPartsRef.current?.(addressParts(place.address_components));
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      listener?.remove();
    };
  }, []);

  return (
    <>
      {/* Keep Google's suggestion list above dialogs, and hide it when it has no items. */}
      <GlobalStyles
        styles={{
          '.pac-container': { zIndex: 1500 },
          '.pac-container:empty': { display: 'none' },
        }}
      />
      <TextField
        {...textFieldProps}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputRef={inputRef}
        autoComplete="off"
      />
    </>
  );
}
